import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { rememberReturnPath } from '@/components/auth/authReturn';
import { AUTH_TEXT_LINK } from '@/components/auth/authStyles';
import { KeepSignedInCheckbox } from '@/components/auth/KeepSignedInCheckbox';
import { LegalLinks } from '@/components/auth/LegalLinks';
import { MfaChallenge } from '@/components/auth/MfaChallenge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FullScreenLoader } from '@/components/ui/full-screen-loader';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/ui/password-input';
import { useAuth } from '@/hooks/use-auth';
import { useToast } from '@/hooks/use-toast';
import { handleApiError } from '@/lib/api';
import { ssoDiscover } from '@/lib/api/auth';
import { recallLoginEmail, rememberLoginEmail } from '@/lib/loginEmailMemory';
import { cn } from '@/lib/utils';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Redirect } from 'wouter';
export default function Login() {
    const { t } = useTranslation();
    const { isAuthenticated, isLoading: authLoading, login } = useAuth();
    const { toast } = useToast();
    // Prefilled from this tab's session so SSO redirects, the forgot-password
    // round trip, and remounts don't force the user to retype their email.
    const [email, setEmail] = useState(() => recallLoginEmail());
    const [password, setPassword] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [mfaChallengeToken, setMfaChallengeToken] = useState(null);
    // true once the user has submitted their email and we've confirmed no SSO
    const [passwordRevealed, setPasswordRevealed] = useState(false);
    // The (trimmed) email the last ssoDiscover ran against. The password step is
    // only valid for this address — editing the email in place invalidates it.
    const [discoveredEmail, setDiscoveredEmail] = useState(null);
    // "Keep me signed in for 30 days": off unless the user asks (see KeepSignedInCheckbox).
    // Offered on the email step so it reaches the SSO path too.
    const [keepSignedIn, setKeepSignedIn] = useState(false);
    // Redirect if already authenticated
    if (authLoading) {
        return _jsx(FullScreenLoader, {});
    }
    if (isAuthenticated) {
        return _jsx(Redirect, { to: "/" });
    }
    /**
     * Run SSO discovery for the current email. An SSO-enforced domain gets a
     * full-document redirect to its IdP; otherwise the vetted address is
     * recorded in discoveredEmail. Fails open on discovery outages — the
     * password path stays usable, and discoveredEmail stays unset so the next
     * submit retries discovery once it recovers.
     */
    const discoverAndMaybeRedirect = async () => {
        // Remember before the discover call: the SSO branch leaves the document,
        // and the email must survive a browser Back from the IdP.
        rememberLoginEmail(email);
        try {
            const result = await ssoDiscover(email, keepSignedIn);
            if (result.ssoEnabled) {
                // Full browser redirect — IdP will eventually POST back to the callback
                // and the ControlPlane will redirect to /auth/sso/finish#handoff=...
                window.location.href = result.authorizationUrl;
                return 'redirected';
            }
            setDiscoveredEmail(email.trim());
        }
        catch {
            // Fail open: let the caller continue to the password path.
        }
        return 'password';
    };
    /**
     * Phase 1 — email submitted. Calls ssoDiscover:
     *   • SSO domain   → full redirect to IdP (authorizationUrl).
     *   • Non-SSO      → reveal the password field inline.
     */
    const handleEmailSubmit = async (e) => {
        e.preventDefault();
        if (!email.trim()) {
            toast({
                title: t('validation.error'),
                description: t('validation.emailRequired'),
                variant: 'destructive',
            });
            return;
        }
        setIsSubmitting(true);
        if ((await discoverAndMaybeRedirect()) === 'password') {
            setPasswordRevealed(true);
        }
        setIsSubmitting(false);
    };
    /**
     * Phase 2 — password submitted (non-SSO path). Runs the existing login flow
     * which already handles the mfaRequired challenge.
     */
    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        if (!email.trim()) {
            toast({
                title: t('validation.error'),
                description: t('validation.emailAndPassword'),
                variant: 'destructive',
            });
            return;
        }
        setIsSubmitting(true);
        try {
            // The password step is only valid for the email discovery approved. The
            // field stays editable, so if the user corrected it in place, re-run
            // discovery first: an SSO domain must go to its IdP, never to a
            // password POST. This runs BEFORE the password-presence check — an
            // SSO-only account has no password to type, so an empty password must
            // not block the redirect.
            if (email.trim() !== discoveredEmail) {
                if ((await discoverAndMaybeRedirect()) === 'redirected')
                    return;
            }
            if (!password) {
                toast({
                    title: t('validation.error'),
                    description: t('validation.emailAndPassword'),
                    variant: 'destructive',
                });
                return;
            }
            // use-auth's login() clears the remembered email on success.
            const outcome = await login({ email, password, rememberMe: keepSignedIn });
            if (outcome.mfaRequired) {
                setMfaChallengeToken(outcome.challengeToken);
                return;
            }
            toast({
                title: t('login.welcomeTitle'),
                description: t('login.welcomeDescription'),
            });
        }
        catch (error) {
            handleApiError(error, {
                title: t('login.failedTitle'),
                statusMessages: { 401: t('login.invalidCredentials') },
            });
        }
        finally {
            setIsSubmitting(false);
        }
    };
    /** Reset to phase 1 so the user can re-enter a different email address. */
    const handleUseADifferentEmail = () => {
        setPasswordRevealed(false);
        setPassword('');
    };
    return (_jsxs("div", { className: "bg-background flex min-h-dvh", children: [_jsxs("div", { className: "relative hidden overflow-hidden lg:flex lg:w-[52%] lg:flex-col lg:justify-center lg:px-[72px]", style: {
                    background: 'radial-gradient(130% 110% at 22% 18%, #0e3b39 0%, #072028 46%, #04121b 100%)',
                }, children: [_jsx("img", { src: "/newlumen-icon-brandgreen.svg", alt: "", "aria-hidden": "true", className: "pointer-events-none absolute -bottom-[70px] -left-[90px] h-[520px] opacity-[0.06]" }), _jsx("div", { "aria-hidden": "true", className: "pointer-events-none absolute -top-[90px] -right-[70px] h-[340px] w-[340px] rounded-full", style: {
                            background: 'radial-gradient(circle, rgba(63,180,168,0.22), transparent 65%)',
                        } }), _jsx("img", { src: "/newlumen-horizontal-duo.svg", alt: "", "aria-hidden": "true", className: "absolute top-11 left-[72px] h-[26px] w-auto" }), _jsxs("div", { className: "relative max-w-[480px]", children: [_jsx("h1", { className: "text-[40px] leading-[1.12] font-bold tracking-tight text-[#f3fbf9]", children: t('login.brandHeadline', "Eyes on the study. We'll handle the rest.") }), _jsx("p", { className: "mt-5 max-w-[430px] text-[15.5px] leading-relaxed text-[#9fc7c1]", children: t('login.brandSubtitle', 'An intelligent worklist, cloud-native viewer, and assisted reporting in one workspace. Less time managing software. More time reading studies.') })] }), _jsxs("span", { className: "absolute bottom-10 left-[72px] text-xs text-[#5f8a85]", children: ["\u00A9", ' ', new Date().getFullYear(), " New Lumen"] })] }), _jsxs("div", { className: "relative flex flex-1 flex-col items-center justify-center gap-6 overflow-hidden p-4", style: {
                    background: 'radial-gradient(80% 70% at 50% 45%, #101314 0%, #0a0c0c 70%)',
                }, children: [_jsx("div", { "aria-hidden": "true", className: "pointer-events-none absolute top-1/2 left-1/2 h-[560px] w-[560px] -translate-x-1/2 -translate-y-1/2", style: {
                            background: 'radial-gradient(circle, rgba(63,180,168,0.12), transparent 60%)',
                        } }), _jsx("div", { className: "relative w-full max-w-[392px] rounded-[20px] p-px", style: {
                            background: 'linear-gradient(160deg, rgba(63,180,168,0.6), rgba(63,180,168,0.05) 42%, rgba(255,255,255,0.05))',
                            boxShadow: '0 0 60px rgba(63,180,168,0.12), 0 24px 60px rgba(0,0,0,0.5)',
                        }, children: _jsxs(Card, { className: "rounded-[19px] border-0 bg-[#15181a]", children: [_jsxs(CardHeader, { className: "space-y-1 text-center", children: [_jsxs("div", { className: "relative mb-4 flex justify-center", children: [_jsx("div", { "aria-hidden": "true", className: "pointer-events-none absolute top-1/2 left-1/2 h-[150px] w-[150px] -translate-x-1/2 -translate-y-1/2 blur-2xl", style: {
                                                        background: 'radial-gradient(circle, rgba(63,180,168,0.4), transparent 64%)',
                                                    } }), _jsx("img", { src: "/newlumen-stacked-duo.svg", alt: "New Lumen", className: "relative h-24 w-auto" })] }), mfaChallengeToken ? (_jsx(CardTitle, { className: "text-2xl font-bold", children: t('login.mfa.title') })) : (_jsx(CardDescription, { children: t('login.subtitle') }))] }), _jsx(CardContent, { children: mfaChallengeToken ? (_jsx(MfaChallenge, { challengeToken: mfaChallengeToken, email: email, onSuccess: () => 
                                        // establishSession (inside MfaChallenge) clears the
                                        // remembered email.
                                        toast({
                                            title: t('login.welcomeTitle'),
                                            description: t('login.welcomeDescription'),
                                        }), onCancel: () => setMfaChallengeToken(null), rememberMe: keepSignedIn })) : (_jsxs("form", { onSubmit: passwordRevealed ? handlePasswordSubmit : handleEmailSubmit, className: "space-y-4", children: [_jsxs("div", { className: "space-y-2", children: [_jsx(Label, { htmlFor: "email", children: t('login.email') }), _jsx(Input, { id: "email", type: "email", placeholder: t('login.emailPlaceholder'), value: email, onChange: (e) => setEmail(e.target.value), disabled: isSubmitting, 
                                                        // The account identifier, not a contact address: password
                                                        // managers offer saved logins on 'username' (the password
                                                        // field is not in the DOM yet on this step).
                                                        autoComplete: "username", enterKeyHint: "next", "data-testid": "email-input" })] }), passwordRevealed && (_jsxs(_Fragment, { children: [_jsx("div", { className: "flex justify-end", children: _jsx("button", { type: "button", onClick: handleUseADifferentEmail, className: cn(AUTH_TEXT_LINK, 'text-muted-foreground text-xs'), "data-testid": "use-different-email", children: t('login.useDifferentEmail') }) }), _jsxs("div", { className: "space-y-2", children: [_jsx(Label, { htmlFor: "password", children: t('login.password') }), _jsx(PasswordInput, { id: "password", placeholder: t('login.passwordPlaceholder'), value: password, onChange: (e) => setPassword(e.target.value), disabled: isSubmitting, autoComplete: "current-password", enterKeyHint: "go", autoFocus: true, "data-testid": "password-input" })] }), _jsx("div", { className: "text-right", children: _jsx(Link, { href: "/forgot-password", className: cn(AUTH_TEXT_LINK, 'text-muted-foreground text-sm'), onClick: () => {
                                                                rememberLoginEmail(email);
                                                                rememberReturnPath('signIn', '/login');
                                                            }, children: t('login.forgotPassword') }) })] })), _jsx(KeepSignedInCheckbox, { checked: keepSignedIn, onCheckedChange: setKeepSignedIn, disabled: isSubmitting }), _jsx(Button, { type: "submit", className: "w-full", loading: isSubmitting, loadingText: passwordRevealed ? t('login.signingIn') : t('login.continuing'), "data-testid": "login-button", children: passwordRevealed ? t('login.signIn') : t('login.continue') })] })) })] }) }), _jsx(LegalLinks, {})] })] }));
}
